import * as React from 'react';

/**
 * Media query that matches when the device has *no* hover capability — i.e. a
 * touch-primary device. This is the precise condition under which hover-based
 * affordances (tooltips) fail, so it, not viewport width, is what decides
 * whether a hint should hover or tap-toggle.
 */
const NO_HOVER_QUERY = '(hover: none)';

/**
 * True on touch-primary (coarse-pointer, no-hover) devices, where hover-based
 * affordances don't work and Radix `Tooltip` "blinks" on tap.
 *
 * Driven entirely by `window.matchMedia` (mirroring {@link useIsMobile}) so it
 * stays deterministic under test — the matchMedia mock in `test/matchMedia.ts`
 * drives it via `setCoarsePointer()`.
 */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = React.useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(NO_HOVER_QUERY).matches : false,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(NO_HOVER_QUERY);
    const onChange = (event: MediaQueryListEvent) => setCoarse(event.matches);
    mql.addEventListener('change', onChange);
    // Re-read in case the environment changed between the initial render and effect.
    setCoarse(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return coarse;
}
